import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Navigate } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { ActivityLogTable } from "@/components/admin/ActivityLogTable";
import { ActivityLogFilters } from "@/components/admin/ActivityLogFilters";
import { ActivityLogExport } from "@/components/admin/ActivityLogExport";
import { Loader2 } from "lucide-react";

export interface ActivityLog {
  id: string;
  user_id: string | null;
  actor_id: string | null;
  action_type: string;
  action_category: string;
  resource_type: string | null;
  resource_id: string | null;
  description: string;
  metadata: any;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  user_profile?: {
    id: string;
    first_name: string;
    last_name: string;
    avatar_url: string;
  } | null;
  actor_profile?: {
    id: string;
    first_name: string;
    last_name: string;
    avatar_url: string;
  } | null;
}

const ActivityLogs = () => {
  const { isAdmin } = useAuth();
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    user_id: "",
    action_category: "",
    action_type: "",
    search: "",
    start_date: "",
    end_date: "",
  });
  const [page, setPage] = useState(0);
  const limit = 50;

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (page * limit).toString(),
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== "")
        ),
      });

      const url = `https://uhtgcidyxprykowgsahq.supabase.co/functions/v1/get-activity-logs?${params.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch activity logs");
      }

      const data = await response.json();
      setLogs(data?.data || []);
      setTotalCount(data?.count || 0);
    } catch (error) {
      console.error("Error fetching activity logs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchLogs();
    }
  }, [isAdmin, page, filters]);

  // Real-time subscription for new logs
  useEffect(() => {
    if (!isAdmin) return;

    const channel = supabase
      .channel("activity_logs_changes")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "user_activity_logs",
        },
        () => {
          // Refetch logs when new activity is logged
          fetchLogs();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAdmin, page, filters]);

  if (!isAdmin) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-4xl font-bold">Activity Logs</h1>
            <ActivityLogExport currentFilters={filters} />
          </div>
          <p className="text-muted-foreground">
            Track all user and admin actions across the system
          </p>
        </div>

        <ActivityLogFilters
          filters={filters}
          onFiltersChange={setFilters}
          onClear={() => {
            setFilters({
              user_id: "",
              action_category: "",
              action_type: "",
              search: "",
              start_date: "",
              end_date: "",
            });
            setPage(0);
          }}
        />

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <ActivityLogTable
            logs={logs}
            totalCount={totalCount}
            page={page}
            limit={limit}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  );
};

export default ActivityLogs;
